// ===========================================================================
// Provider Documentation Coach — datos fake deterministas (Módulo F).
//
// No existe backend todavía. Cuando exista, este archivo se elimina y el
// service consumirá el schema `analytics` (TAB_DOC_FEEDBACK /
// TAB_PROVIDER_DOC_SCORE) vía /api/analytics/*.
// Ver docs/modulos/BillingV2/MODULO_F_PROVIDER_DOC_COACH.md
// ===========================================================================

import { NOTE_STATUS, ISSUE_TYPES } from "../common/constants";

/**
 * Notas cerradas con hallazgos de documentación detectados por la capa NLP.
 * Cada nota: excerpt fake, issues con severidad, sugerencia de código
 * (from → to) y tips educativos (feedback no punitivo).
 */
export const FAKE_NOTES = [
  {
    id: "NOTE-5101",
    noteDate: "2026-06-10",
    provider: "Dr. R. Alonso",
    patientName: "Maria Garcia",
    cipId: "CIP-204881",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 58,
    excerpt:
      "A/P: 1. Diabetes mellitus type 2 (E11.9) — continue metformin 1000 mg BID. Patient reports burning sensation in both feet, worse at night. Monofilament exam decreased bilaterally. 2. HTN — stable on lisinopril. Recheck labs in 3 months.",
    issues: [
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "high",
        description:
          "E11.9 documented while exam and symptoms support diabetic polyneuropathy.",
      },
      {
        type: ISSUE_TYPES.MISSING_LINKAGE,
        severity: "medium",
        description:
          "Neuropathy findings not linked to diabetes with causal language ('due to', 'secondary to').",
      },
    ],
    suggestion: {
      fromCode: "E119",
      fromDesc: "T2DM without complications",
      toCode: "E1142",
      toDesc: "T2DM with diabetic polyneuropathy",
      rationale:
        "Plan documents burning feet + abnormal monofilament exam. Linking both supports E11.42 (HCC 37 vs HCC 38).",
    },
    coachingTips: [
      "Use causal language: 'polyneuropathy due to type 2 diabetes'.",
      "Document Evaluation (monofilament result) and Treatment (gabapentin or referral) for MEAT.",
    ],
  },
  {
    id: "NOTE-5102",
    noteDate: "2026-06-10",
    provider: "Dr. K. Patel",
    patientName: "Rosa Linares",
    cipId: "CIP-211504",
    noteType: "Hospital Discharge Follow Up",
    status: NOTE_STATUS.OPEN,
    docScore: 61,
    excerpt:
      "Seen after recent hospitalization for CHF exacerbation. Lungs clear today, no edema. Continue home meds. Heart failure (I50.9). Follow up 2 weeks.",
    issues: [
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "high",
        description:
          "I50.9 unspecified; discharge summary documents acute on chronic systolic heart failure.",
      },
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "medium",
        description:
          "No Assessment of current HF status (NYHA class, volume status) beyond 'lungs clear'.",
      },
    ],
    suggestion: {
      fromCode: "I509",
      fromDesc: "Heart failure, unspecified",
      toCode: "I5023",
      toDesc: "Acute on chronic systolic HF",
      rationale:
        "Hospital documentation supports systolic type and acuity; carry specificity into the office note.",
    },
    coachingTips: [
      "Pull EF and HF type from the echo into the assessment.",
      "Document NYHA class at every HF visit — it satisfies Assessment for MEAT.",
    ],
  },
  {
    id: "NOTE-5103",
    noteDate: "2026-06-09",
    provider: "Dr. M. Fuentes",
    patientName: "Walter Briggs",
    cipId: "CIP-176342",
    noteType: "TELEHEALTH_2.0",
    status: NOTE_STATUS.COACHED,
    docScore: 72,
    excerpt:
      "Telehealth visit. CKD (N18.9) — stable. Continue current management. BP 132/78 reported at home. Will repeat labs next month.",
    issues: [
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "high",
        description: "N18.9 unspecified; last two eGFR values (44, 48) support stage 3b.",
      },
    ],
    suggestion: {
      fromCode: "N189",
      fromDesc: "CKD, unspecified",
      toCode: "N1832",
      toDesc: "CKD stage 3b",
      rationale: "Two eGFR results 30-44 over 90+ days establish stage 3b (HCC 329).",
    },
    coachingTips: [
      "State the eGFR value and date next to the CKD diagnosis.",
      "Stage CKD explicitly at least once per year.",
    ],
  },
  {
    id: "NOTE-5104",
    noteDate: "2026-06-09",
    provider: "Dr. R. Alonso",
    patientName: "Albert Nguyen",
    cipId: "CIP-187990",
    noteType: "Annual Wellness 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 55,
    excerpt:
      "AWV completed. PHQ-9 score 14 documented by MA. Patient reports low mood and poor sleep for several months. Plan: continue current medications, return in 6 months.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "high",
        description:
          "Positive PHQ-9 (14) with symptoms but no diagnosis, no Treatment and no follow-up plan documented.",
      },
      {
        type: ISSUE_TYPES.MISSING_LINKAGE,
        severity: "low",
        description: "Sleep disturbance not connected to mood assessment.",
      },
    ],
    suggestion: {
      fromCode: "—",
      fromDesc: "No diagnosis coded",
      toCode: "F329",
      toDesc: "Major depressive disorder, single episode",
      rationale:
        "PHQ-9 of 14 + reported symptoms support a depression diagnosis if clinically confirmed (HCC 155).",
    },
    coachingTips: [
      "A positive screening requires a documented clinical decision: diagnose, monitor or refer.",
      "Add Treatment (med, therapy referral) and a follow-up interval shorter than 6 months.",
    ],
  },
  {
    id: "NOTE-5105",
    noteDate: "2026-06-08",
    provider: "Dr. K. Patel",
    patientName: "Carmen Soto",
    cipId: "CIP-225617",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.ACKNOWLEDGED,
    docScore: 84,
    excerpt:
      "OUD in sustained remission, continues monthly counseling. BP 148/92 today, repeat 150/90. Discussed lifestyle; continue lisinopril 20 mg. Recheck 2 weeks.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "low",
        description:
          "Hypertension plan lacks explicit Evaluation element (no review of home readings).",
      },
    ],
    suggestion: {
      fromCode: "I10",
      fromDesc: "Essential hypertension",
      toCode: "I10",
      toDesc: "Essential hypertension (documentation only)",
      rationale: "Code is correct; strengthen MEAT by documenting home BP log review.",
    },
    coachingTips: [
      "Reference the home BP log explicitly to complete Evaluation.",
    ],
  },
  {
    id: "NOTE-5106",
    noteDate: "2026-06-08",
    provider: "Dr. M. Fuentes",
    patientName: "Beatriz Camacho",
    cipId: "CIP-217554",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 49,
    excerpt:
      "COPD stable on budesonide/formoterol. Uses home oxygen at night and with exertion since March. SpO2 89% on room air today. Continue current regimen.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_LINKAGE,
        severity: "high",
        description:
          "Home O2 dependence + SpO2 89% documented without coding chronic respiratory failure.",
      },
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "medium",
        description: "COPD coded J44.9 without exacerbation/severity context.",
      },
    ],
    suggestion: {
      fromCode: "J449",
      fromDesc: "COPD, unspecified",
      toCode: "J9611",
      toDesc: "Chronic respiratory failure with hypoxia",
      rationale:
        "Chronic home O2 + resting hypoxemia support J96.11 in addition to COPD (HCC 211).",
    },
    coachingTips: [
      "When a patient is on chronic home O2, document and code the underlying respiratory failure.",
      "Record liters/min and hours of daily use.",
    ],
  },
  {
    id: "NOTE-5107",
    noteDate: "2026-06-07",
    provider: "Dr. R. Alonso",
    patientName: "Hector Maldonado",
    cipId: "CIP-203145",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.COACHED,
    docScore: 66,
    excerpt:
      "DM2 — A1c 8.4, on glargine. eGFR 48 noted. Renal function trending down. Continue meds, nephrology referral placed.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_LINKAGE,
        severity: "medium",
        description:
          "Declining renal function with diabetes documented but CKD not linked as diabetic complication (E11.22).",
      },
    ],
    suggestion: {
      fromCode: "E119",
      fromDesc: "T2DM without complications",
      toCode: "E1122",
      toDesc: "T2DM with diabetic CKD",
      rationale: "eGFR 48 + diabetes: code E11.22 with N18.3 for full specificity.",
    },
    coachingTips: [
      "Pair E11.22 with the CKD stage code (N18.x) — both are required.",
    ],
  },
  {
    id: "NOTE-5108",
    noteDate: "2026-06-07",
    provider: "Dr. L. Romero",
    patientName: "Samuel Ortega",
    cipId: "CIP-228430",
    noteType: "Annual Wellness 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 52,
    excerpt:
      "AWV. Drinks 4-5 beers daily. Platelets low on recent labs, mild transaminitis. Abdomen soft. Counseling on alcohol provided. Routine follow-up.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "high",
        description:
          "Alcohol use disorder pattern documented without diagnosis, assessment or treatment plan.",
      },
      {
        type: ISSUE_TYPES.MISSING_LINKAGE,
        severity: "medium",
        description:
          "Thrombocytopenia + transaminitis not evaluated against possible liver disease.",
      },
    ],
    suggestion: {
      fromCode: "—",
      fromDesc: "No diagnosis coded",
      toCode: "F1020",
      toDesc: "Alcohol use disorder, moderate",
      rationale:
        "Daily use with lab findings supports AUD diagnosis and further liver workup (HCC 137).",
    },
    coachingTips: [
      "Quantify use (AUDIT-C) and document the clinical assessment, not only counseling.",
      "Order liver imaging when labs suggest hepatic involvement.",
    ],
  },
  {
    id: "NOTE-5109",
    noteDate: "2026-06-06",
    provider: "Dr. L. Romero",
    patientName: "Lucia Trevino",
    cipId: "CIP-191236",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 63,
    excerpt:
      "Parkinson's — tremor controlled on carbidopa/levodopa. Two falls last month per daughter. Gait slow. Continue meds.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "medium",
        description:
          "Falls reported without assessment or plan (PT referral, home safety) — Treatment element missing.",
      },
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "low",
        description: "Repeated falls (R29.6) not coded despite documentation.",
      },
    ],
    suggestion: {
      fromCode: "G20",
      fromDesc: "Parkinson's disease",
      toCode: "G20",
      toDesc: "Parkinson's + R29.6 repeated falls",
      rationale: "Add R29.6 and a fall-prevention plan; supports HEDIS FRM as well.",
    },
    coachingTips: [
      "Falls mentioned anywhere in the note should generate an assessment and plan.",
    ],
  },
  {
    id: "NOTE-5110",
    noteDate: "2026-06-06",
    provider: "Dr. K. Patel",
    patientName: "Felix Arroyo",
    cipId: "CIP-232901",
    noteType: "TELEHEALTH_2.0",
    status: NOTE_STATUS.ACKNOWLEDGED,
    docScore: 88,
    excerpt:
      "Prostate cancer on active surveillance. PSA 6.8 reviewed, urology follow-up scheduled. Patient tolerating surveillance plan well. C61 active.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "low",
        description: "Monitor element present; consider documenting next PSA interval explicitly.",
      },
    ],
    suggestion: {
      fromCode: "C61",
      fromDesc: "Prostate cancer",
      toCode: "C61",
      toDesc: "Prostate cancer (documentation complete)",
      rationale: "Coding correct; minor MEAT reinforcement only.",
    },
    coachingTips: ["State the planned PSA recheck date to close the Monitor element."],
  },
  {
    id: "NOTE-5111",
    noteDate: "2026-06-05",
    provider: "Dr. M. Fuentes",
    patientName: "Oscar Villalba",
    cipId: "CIP-208119",
    noteType: "Annual Wellness 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 60,
    excerpt:
      "AWV. Hx of MI 2023, on aspirin and atorvastatin 20. No chest pain. ECG today unremarkable. Continue meds.",
    issues: [
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "medium",
        description: "History of MI documented in narrative but I25.2 not coded.",
      },
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "low",
        description: "Statin intensity decision not documented despite ASCVD history.",
      },
    ],
    suggestion: {
      fromCode: "—",
      fromDesc: "No diagnosis coded",
      toCode: "I252",
      toDesc: "Old myocardial infarction",
      rationale: "Code the documented MI history; it informs risk adjustment and SPC measure.",
    },
    coachingTips: [
      "Chronic/historical conditions assessed during AWV must be coded, not just narrated.",
    ],
  },
  {
    id: "NOTE-5112",
    noteDate: "2026-06-05",
    provider: "Dr. L. Romero",
    patientName: "Gloria Pineda",
    cipId: "CIP-214772",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.COACHED,
    docScore: 70,
    excerpt:
      "RA on methotrexate, joints stable. Last LFTs December. Continue MTX 15 weekly. Return 3 months.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "medium",
        description:
          "Monitor element stale: DMARD monitoring labs not ordered in over 5 months.",
      },
      {
        type: ISSUE_TYPES.UNSPECIFIED_CODE,
        severity: "low",
        description: "RA coded M06.9 unspecified; seropositivity known from 2024 labs (M05.x).",
      },
    ],
    suggestion: {
      fromCode: "M069",
      fromDesc: "RA, unspecified",
      toCode: "M0580",
      toDesc: "Seropositive RA",
      rationale: "RF+ documented in 2024; M05.80 is more specific (same HCC, better data).",
    },
    coachingTips: ["Order LFT/CBC for MTX monitoring at this visit and document it as plan."],
  },
  {
    id: "NOTE-5113",
    noteDate: "2026-06-04",
    provider: "Dr. R. Alonso",
    patientName: "James Holt",
    cipId: "CIP-198223",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.OPEN,
    docScore: 57,
    excerpt:
      "Breathing okay, still smoking 1/2 pack. Lungs with mild wheeze. Refill tiotropium. Return 6 months.",
    issues: [
      {
        type: ISSUE_TYPES.MISSING_MEAT,
        severity: "high",
        description: "COPD treated (tiotropium) but never assessed or coded in this note.",
      },
      {
        type: ISSUE_TYPES.MISSING_LINKAGE,
        severity: "low",
        description: "Active smoking not coded (F17.210) nor linked to cessation counseling.",
      },
    ],
    suggestion: {
      fromCode: "—",
      fromDesc: "No diagnosis coded",
      toCode: "J449",
      toDesc: "COPD, unspecified",
      rationale:
        "Medication and exam findings establish COPD; code it with current status (HCC 280).",
    },
    coachingTips: [
      "Every refilled chronic med should map to a coded diagnosis in the same note.",
      "Add F17.210 with cessation counseling for complete capture.",
    ],
  },
  {
    id: "NOTE-5114",
    noteDate: "2026-06-04",
    provider: "Dr. K. Patel",
    patientName: "Dolores Ferrer",
    cipId: "CIP-219008",
    noteType: "Follow Up Exam 2.0",
    status: NOTE_STATUS.ACKNOWLEDGED,
    docScore: 91,
    excerpt:
      "Osteoporosis with recent wrist fracture. DXA ordered, started alendronate 70 weekly + vitamin D. Fall risk reviewed, PT referral placed. M80.0 series coded.",
    issues: [],
    suggestion: null,
    coachingTips: [],
  },
];

/**
 * Score agregado por provider (specificity, MEAT completeness, HCC perdidos).
 * Backend futuro: analytics.TAB_PROVIDER_DOC_SCORE.
 */
export const FAKE_PROVIDER_SCORES = [
  { provider: "Dr. R. Alonso", notesReviewed: 48, specificity: 74, meat: 69, missedHcc: 11, trend: "▲ 3%" },
  { provider: "Dr. K. Patel", notesReviewed: 42, specificity: 86, meat: 81, missedHcc: 4, trend: "▲ 5%" },
  { provider: "Dr. M. Fuentes", notesReviewed: 37, specificity: 71, meat: 73, missedHcc: 9, trend: "▼ 2%" },
  { provider: "Dr. L. Romero", notesReviewed: 29, specificity: 65, meat: 62, missedHcc: 14, trend: "▲ 1%" },
];
